// ui/presenters/fundingSourceDraft.js
import { carriesPriorAuthorization } from '../../domain/constants/fundingSource';
import { exceedsLine, withPersistedRules } from '../../domain/services/modifierRules';
import { hasRuleProblem } from './modifierRuleAuthoring';

/**
 * A funding source as the edit form holds it, in the MODEL's vocabulary rather than the document's.
 *
 * The form used to hold the stored shape, because the settings page read and wrote documents
 * directly and there was no write-side mapper. There is one now, so the form speaks `name` and
 * `coveredServices` and the mapper does the translating — which is what stops the document's field
 * names spreading into a component again.
 *
 * OUT HERE RATHER THAN IN THE DIALOG so it can be tested. It is the one function the dialog's
 * ghost-version guard depends on: the guard compares a draft against a baseline built by this same
 * function, so any field this reads differently from the mapper makes a freshly opened form report
 * itself as edited. That is not something a component test would catch by accident, and it shipped
 * once already.
 *
 * `isActive` is carried rather than edited: retiring is a row action, and a version carries the
 * whole record, so a save that dropped the flag would quietly reactivate a retired payer.
 *
 * @param {import('../../domain/model/FundingSource.types').FundingSource|null} record
 * @returns {Object} a fresh object every call — the draft and the baseline must not share one
 */
export function toDraft(record) {
  return {
    name: record?.name ?? '',
    isActive: record?.isActive ?? true,
    archetype: record?.archetype ?? null,
    billingChannel: record?.billingChannel ?? null,
    // OFF where the channel asks at all, which covers a new funder and one written before the field
    // existed. Off means authorization is required — the norm — so the exception is never inherited
    // by accident. See the model for why the field is named for the exception, not the rule.
    //
    // NULL WHERE THE CHANNEL CANNOT EXPRESS IT, which is the half that has to agree with the mapper.
    // A flat `?? false` did not: the mapper reads null for an invoice or a roster, `?? false` made
    // the baseline false, and the dialog's channel watcher then cleared the draft back to null — so
    // the form was dirty before the user touched it, and the save it enabled appended a version that
    // decided nothing.
    billWithoutAuthorization: carriesPriorAuthorization(record?.billingChannel ?? null)
      ? record?.billWithoutAuthorization ?? false
      : null,
    // NOT defaulted, unlike the switch above. `performer` is the commonest answer, not a safe one —
    // a funder that must bill under a supervising provider would be silently marked otherwise and
    // produce a claim naming an NPI the payer rejects. So an unanswered control renders unanswered,
    // and the save is refused until somebody chooses.
    billsUnder: record?.billsUnder ?? null,
    notes: record?.notes ?? '',
    coveredServices: (record?.coveredServices ?? []).map((entry) => ({
      ...entry,
      // Copied rather than shared, for the same reason the entry is: the draft and the baseline must
      // not hold one array, or editing the draft would edit what it is compared against and the
      // form would never report itself as dirty.
      modifiers: [...(entry.modifiers ?? [])],
    })),
    // Absent means the payer wants no modifier, which is also what a payer nobody has configured
    // wants. There is deliberately no third state, so `?? null` is the whole rule.
    telehealthModifier: record?.telehealthModifier ?? null,
    placeOfServiceOverrides: (record?.placeOfServiceOverrides ?? []).map((entry) => ({ ...entry })),
  };
} // toDraft()

/**
 * An entry list reduced to something order-independent and comparable.
 *
 * Serializes each entry's own fields rather than just its id, so a per-entry edit (a modifier set, an
 * override's code) counts. Entries are sorted because neither picker promises an order and a reorder
 * of the LIST is not an edit; the order WITHIN a modifier set is preserved, because payers care which
 * modifier comes first.
 *
 * SERIALIZED AS JSON RATHER THAN JOINED WITH DELIMITERS, because a modifier is whatever somebody
 * typed. Joining on a comma made the modifier set ['A', 'B'] and the single modifier 'A,B' produce
 * the same key, so replacing one with the other left Save disabled on a real edit. Any delimiter has
 * the same hole, since none is illegal in a value the payer defines.
 */
const entryKeys = (entries) => {
  return entries
    .map((entry) => JSON.stringify(Object.keys(entry).sort().map((key) => [key, entry[key]])))
    .sort()
    .join('|');
};

/**
 * The length is compared as well as the key, because an entry with no fields serializes to an empty
 * string and would otherwise make `[{}]` and `[]` indistinguishable.
 */
const sameEntries = (current, original) => {
  return current.length === original.length && entryKeys(current) === entryKeys(original);
};

/**
 * Overrides that would actually be written, which is what a change has to be measured against.
 *
 * The editor's draft holds an override from the moment a place is picked, before any code is typed,
 * and the mapper drops a codeless one on write. Counting that row as a change would arm Save for an
 * edit that stores nothing — a ghost version by the very route the guard exists to close.
 */
const persistedOverrides = (overrides) => {
  return overrides.filter((entry) => (entry.placeOfServiceCode ?? '').trim().length > 0);
};

/**
 * Has the user changed anything worth recording?
 *
 * GUARDS AGAINST GHOST VERSIONS. Saving an unchanged record appends a version that decided nothing —
 * and on a versioned timeline that is worse than clutter: it takes a version number, shows up as an
 * amendment of the date it lands on, and makes the history claim somebody revised the payer when they
 * only opened the dialog.
 *
 * COMPARED FIELD BY FIELD, NOT BY DEEP EQUALITY, so that adding a field to the draft forces somebody
 * to decide whether it counts. The cost of that choice is exactly one failure mode, and it has now
 * happened: `telehealthModifier` and `placeOfServiceOverrides` were added to the draft and not here,
 * so a payer whose ONLY edit was a telehealth modifier or a place override could not be saved at all —
 * the form sat clean while the screen showed the change. It lived in the dialog then, where nothing
 * could test it. It lives here now, beside the `toDraft` it compares, and every field is covered.
 *
 * @param {Object} draft
 * @param {Object} baseline
 * @returns {boolean}
 */
export function isDraftDirty(draft, baseline) {
  return draft.name.trim() !== baseline.name.trim()
    || draft.notes !== baseline.notes
    || draft.archetype !== baseline.archetype
    || draft.billingChannel !== baseline.billingChannel
    || draft.billWithoutAuthorization !== baseline.billWithoutAuthorization
    || draft.billsUnder !== baseline.billsUnder
    || draft.isActive !== baseline.isActive
    || draft.telehealthModifier !== baseline.telehealthModifier
    || !sameEntries(
      draft.coveredServices.map(withPersistedRules),
      baseline.coveredServices.map(withPersistedRules),
    )
    || !sameEntries(
      persistedOverrides(draft.placeOfServiceOverrides),
      persistedOverrides(baseline.placeOfServiceOverrides),
    );
} // isDraftDirty()

/**
 * The services whose rules would put a fifth modifier on a claim line.
 *
 * THE AUTHORED SET IS NOT THE WHOLE LINE, which is why no single control can catch this. At a
 * telehealth place the payer's telehealth modifier joins it, and a conditional rule adds whatever the
 * performer's tier earns — three fields, filled in any order, none of which can see the other two.
 *
 * A service whose own set already NAMES what the others would add is fine: the resolver
 * de-duplicates, so the line still fits. The counting is in `worstCaseLineLength`, which takes the
 * longest SINGLE row rather than the sum of them, because exactly one row matches.
 *
 * Reported for every service rather than only the ones actually delivered by telehealth, because
 * nothing on a funding source says which those are, and the two directions fail differently: a cap one
 * short is visible and arguable while somebody is authoring, where a fifth modifier is a rejected
 * claim that surfaces weeks later as a denial.
 *
 * @param {Object} draft
 * @returns {Set<string>} treatment service ids
 */
export function servicesExceedingLine(draft) {
  return new Set(draft.coveredServices
    .filter((entry) => exceedsLine(entry, draft.telehealthModifier))
    .map((entry) => entry.treatmentServiceId));
} // servicesExceedingLine()

/**
 * The services whose conditional rules cannot be saved as they stand.
 *
 * SEPARATE FROM `servicesExceedingLine` although both disable Save, because the two say different
 * things to the reader: one is a combination that will not fit on a claim line, the other is a rule
 * that does not mean what it looks like it means. Collapsing them would make the row caption — the
 * only thing a shut row can say — name the wrong fault.
 *
 * @param {Object} draft
 * @returns {Set<string>} treatment service ids
 */
export function servicesWithRuleProblems(draft) {
  return new Set(draft.coveredServices
    .filter(hasRuleProblem)
    .map((entry) => entry.treatmentServiceId));
} // servicesWithRuleProblems()
